import {
  useFormErrorNotif,
  useFormSuccessNotification,
} from "@babackapp/react-hook-forms-components/form-notifications";
import { useSuperAdminAllShops } from "@babackapp/private-api-client/client/use-super-admin-all-shops";
import useDuplicateShopSettings from "@babackapp/private-api-client/client/use-duplicate-shop-settings";
import useDuplicateShopSettingsPreview, {
  type DuplicateSettingsPreviewCarrier,
  type DuplicateShopSettingsPreview,
} from "@babackapp/private-api-client/client/use-duplicate-shop-settings-preview";
import { Button } from "@babackapp/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@babackapp/ui/components/dialog";
import { Label } from "@babackapp/ui/components/label";
import {
  RadioGroup,
  RadioGroupItem,
} from "@babackapp/ui/components/radio-group";
import { LoadingButton } from "@babackapp/ui/components/loading-button";
import { MultiSelect } from "@babackapp/ui/components/multi-select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@babackapp/ui/components/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@babackapp/ui/components/command";
import { CopyIcon } from "@radix-ui/react-icons";
import { Check, ChevronsUpDown } from "lucide-react";
import { useState } from "react";

interface Props {
  sourceShopId: string;
  sourceShopName: string;
  sourcePlanReturnToStore?: boolean | null;
}

interface SelectionState {
  carriers: boolean;
  rules: boolean;
  preferences: boolean;
  users: boolean;
  exchange: boolean;
  discount: boolean;
  reasons: boolean;
  disapprovalReasons: boolean;
  integrations: boolean;
  returnToStore: boolean;
}

type Step = "select" | "confirm" | "codeConflicts";

type CarriersMode = "replace" | "append";

const CARRIERS_MODES: { value: CarriersMode; label: string }[] = [
  {
    value: "replace",
    label: "Remplacer les transporteurs de la boutique cible",
  },
  {
    value: "append",
    label: "Ajouter aux transporteurs de la boutique cible",
  },
];

type CodeConflicts = "skip" | "replace";

const CODE_CONFLICTS_CHOICES: { value: CodeConflicts; label: string }[] = [
  {
    value: "skip",
    label:
      "Garder ceux de la boutique cible (ceux de la source ne sont pas copiés)",
  },
  {
    value: "replace",
    label: "Les remplacer par ceux de la boutique source",
  },
];

type ApiError =
  | { code?: string; message?: string; codes?: string[] }
  | undefined;

const CODE_CONFLICTS_ERROR = "duplicate-settings-carrier-code-conflicts";

// Sections the preview reports as overwritten on the target.
const SECTION_LABELS: Record<string, string> = {
  overrideRules: "Règles d'exception",
  routingLocationRules: "Règles de routage",
  preferences: "Paramètres de la boutique",
  exchange: "Configuration des échanges",
  discount: "Configuration des avoirs / remises",
  reasons: "Raisons de retour",
  disapprovalReasons: "Motifs de non-approbation",
  integrations: "Intégrations (hors Stripe)",
  returnToStore: "Configuration retour en boutique",
};

function formatCarriers(carriers: DuplicateSettingsPreviewCarrier[]) {
  return carriers
    .map(({ code, title }) => (title ? `${title} (${code})` : code))
    .join(", ");
}

// A template can fill several slots: listed once.
function formatTemplateTitles(templates: { title: string }[]) {
  return [...new Set(templates.map(({ title }) => title))].join(", ");
}

// Refusals of the backend (BBS-383): nothing was written on the target.
function conflictMessage(error: ApiError) {
  if (error?.code === "duplicate-settings-would-orphan-override-rules")
    return "Des règles d'éligibilité de la boutique cible visent des transporteurs qui seraient supprimés : elles deviendraient inopérantes. Dupliquez aussi les règles d'éligibilité, ou gardez les transporteurs de la cible. Rien n'a été modifié.";
  if (error?.code === "duplicate-settings-target-changed")
    return "La boutique cible a été modifiée pendant la duplication. Rien n'a été modifié : relancez la duplication.";
  if (error?.code === "duplicate-settings-override-rules-need-carriers")
    return "Des règles d'éligibilité de la boutique source visent des transporteurs ou des tarifs qui n'existeraient pas sur la cible : dupliquez aussi les transporteurs, en remplaçant ceux de même code. Rien n'a été modifié.";
  return undefined;
}

const BASE_CATEGORIES: { key: keyof SelectionState; label: string }[] = [
  { key: "carriers", label: "Transporteurs (avec credentials)" },
  { key: "rules", label: "Règles d'éligibilité et de routage" },
  { key: "preferences", label: "Paramètres de la boutique" },
  { key: "exchange", label: "Configuration des échanges" },
  { key: "discount", label: "Configuration des avoirs / remises" },
  { key: "reasons", label: "Raisons de retour" },
  { key: "disapprovalReasons", label: "Motifs de non-approbation" },
  { key: "integrations", label: "Intégrations (hors Stripe)" },
  { key: "users", label: "Utilisateurs (invitation)" },
];

const RETURN_TO_STORE_CATEGORY: { key: keyof SelectionState; label: string } = {
  key: "returnToStore",
  label: "Configuration retour en boutique",
};

const DEFAULT_SELECTION: SelectionState = {
  carriers: true,
  rules: true,
  preferences: true,
  users: false,
  exchange: false,
  discount: false,
  reasons: false,
  disapprovalReasons: false,
  integrations: false,
  returnToStore: false,
};

export default function DuplicateShopSettingsDialog({
  sourceShopId,
  sourceShopName,
  sourcePlanReturnToStore,
}: Props) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<Step>("select");
  const [targetShopId, setTargetShopId] = useState<string>("");
  const [isShopPickerOpen, setIsShopPickerOpen] = useState(false);
  const [selection, setSelection] = useState<SelectionState>(DEFAULT_SELECTION);
  const [carriersMode, setCarriersMode] = useState<CarriersMode>("replace");
  const [conflictingCodes, setConflictingCodes] = useState<string[]>([]);
  const [codeConflicts, setCodeConflicts] = useState<CodeConflicts>("skip");
  // The choice the preview was computed with, sent again on Apply.
  const [resolution, setResolution] = useState<CodeConflicts | undefined>();
  const [preview, setPreview] = useState<DuplicateShopSettingsPreview>();

  const categories = sourcePlanReturnToStore
    ? [...BASE_CATEGORIES, RETURN_TO_STORE_CATEGORY]
    : BASE_CATEGORIES;

  // Staff-only tool (SuperAdminGuard): any shop is a valid target, not only
  // the ones the super admin is a member of — none since BAB-3035.
  const { data: shops, isPending: isLoadingShops } =
    useSuperAdminAllShops(open);
  const { mutateAsync: duplicateSettings, isPending } =
    useDuplicateShopSettings(sourceShopId);
  const { mutateAsync: previewSettings, isPending: isPreviewing } =
    useDuplicateShopSettingsPreview(sourceShopId);

  const successNotification = useFormSuccessNotification();
  const errorNotification = useFormErrorNotif();

  const targetShops = shops?.filter((s) => s.id !== sourceShopId) ?? [];

  const targetShop = targetShops.find((s) => s.id === targetShopId);

  const selectedCategories = categories.filter((c) => selection[c.key]);

  const handleSelectionChange = (keys: string[]) =>
    setSelection((prev) => {
      const next = { ...prev };
      for (const { key } of categories) next[key] = keys.includes(key);
      return next;
    });

  const canProceed = targetShopId !== "" && selectedCategories.length > 0;

  const handleReset = () => {
    setStep("select");
    setTargetShopId("");
    setIsShopPickerOpen(false);
    setSelection(DEFAULT_SELECTION);
    setCarriersMode("replace");
    setConflictingCodes([]);
    setCodeConflicts("skip");
    setResolution(undefined);
    setPreview(undefined);
  };

  const handleOpenChange = (value: boolean) => {
    setOpen(value);
    if (!value) handleReset();
  };

  const buildBody = (carrierCodeConflicts?: CodeConflicts) => ({
    targetShopId,
    carriers: selection.carriers || undefined,
    carriersMode: selection.carriers ? carriersMode : undefined,
    carrierCodeConflicts,
    rules: selection.rules || undefined,
    preferences: selection.preferences || undefined,
    users: selection.users || undefined,
    exchange: selection.exchange || undefined,
    discount: selection.discount || undefined,
    reasons: selection.reasons || undefined,
    disapprovalReasons: selection.disapprovalReasons || undefined,
    integrations: selection.integrations || undefined,
    returnToStore: selection.returnToStore || undefined,
  });

  // Refusals of the backend, at preview or apply time: nothing was written.
  const handleRefusal = (error: unknown) => {
    const apiError = error as ApiError;
    // Carriers whose code the target already has: the operator decides,
    // then the preview runs again with the choice.
    if (apiError?.code === CODE_CONFLICTS_ERROR && apiError.codes) {
      setConflictingCodes(apiError.codes);
      setStep("codeConflicts");
      return;
    }
    const message = conflictMessage(apiError);
    errorNotification(message ? { message } : undefined);
  };

  // What the duplication would delete or overwrite, shown before Apply.
  const handlePreview = async (choice?: CodeConflicts) => {
    try {
      const result = (await previewSettings(
        buildBody(choice),
      )) as unknown as DuplicateShopSettingsPreview;
      setPreview(result);
      setResolution(choice);
      setStep("confirm");
    } catch (error) {
      handleRefusal(error);
    }
  };

  const handleConfirm = async () => {
    try {
      await duplicateSettings(buildBody(resolution));
      successNotification();
      setOpen(false);
      handleReset();
    } catch (error) {
      handleRefusal(error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <CopyIcon className="h-4 w-4" />
          Dupliquer ces paramètres sur une autre boutique
        </Button>
      </DialogTrigger>

      {/* A one-track grid sized to the dialog: an `auto` track grows to the
          widest unbreakable line (a long shop name) and scrolls sideways. */}
      <DialogContent className="max-w-md grid-cols-[minmax(0,1fr)]">
        {step === "select" ? (
          <>
            <DialogHeader>
              <DialogTitle>Dupliquer les paramètres</DialogTitle>
            </DialogHeader>

            <div className="space-y-5 py-2">
              <div className="space-y-2">
                <Label>Boutique cible</Label>
                <Popover
                  open={isShopPickerOpen}
                  onOpenChange={setIsShopPickerOpen}
                  // Inside the Dialog: without its own scroll lock, the
                  // portalled list ignores the wheel.
                  modal
                >
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      role="combobox"
                      aria-expanded={isShopPickerOpen}
                      className="w-full justify-between font-normal"
                    >
                      {targetShop ? (
                        <span className="flex min-w-0 items-baseline gap-2">
                          <span className="max-w-[60%] shrink-0 truncate">
                            {targetShop.name}
                          </span>
                          <span className="truncate text-xs text-muted-foreground">
                            {targetShop.shopifyUrl ?? targetShop.id}
                          </span>
                        </span>
                      ) : (
                        <span className="text-muted-foreground">
                          Choisir une boutique…
                        </span>
                      )}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-(--radix-popover-trigger-width) p-0"
                    align="start"
                  >
                    <Command>
                      <CommandInput placeholder="Nom, domaine Shopify ou id…" />
                      <CommandList className="max-h-64">
                        {isLoadingShops ? (
                          <p className="p-3 text-sm text-muted-foreground">
                            Chargement des boutiques…
                          </p>
                        ) : (
                          <>
                            <CommandEmpty>Aucune boutique.</CommandEmpty>
                            <CommandGroup>
                              {targetShops.map((shop) => (
                                <CommandItem
                                  key={shop.id}
                                  value={`${shop.name}|${shop.shopifyUrl ?? ""}|${shop.id}`}
                                  onSelect={() => {
                                    setTargetShopId(shop.id);
                                    setIsShopPickerOpen(false);
                                  }}
                                  className="gap-2"
                                >
                                  <Check
                                    className={
                                      shop.id === targetShopId
                                        ? "h-4 w-4 shrink-0"
                                        : "h-4 w-4 shrink-0 opacity-0"
                                    }
                                  />
                                  <span className="min-w-0 flex-1 truncate">
                                    {shop.name}
                                  </span>
                                  <span className="max-w-[50%] truncate text-xs text-muted-foreground">
                                    {shop.shopifyUrl ?? shop.id}
                                  </span>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </>
                        )}
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label>Que souhaitez-vous copier ?</Label>
                <MultiSelect
                  options={categories.map(({ key, label }) => ({
                    value: key,
                    label,
                  }))}
                  value={selectedCategories.map(({ key }) => key)}
                  onChange={handleSelectionChange}
                  placeholder="Choisir les paramètres à copier…"
                  contentClassName="w-(--radix-popover-trigger-width)"
                  modal
                  hasRemovableChips={false}
                />
              </div>

              {selection.carriers && (
                <div className="space-y-2">
                  <Label>Transporteurs</Label>
                  <RadioGroup
                    value={carriersMode}
                    onValueChange={(value) =>
                      setCarriersMode(value as CarriersMode)
                    }
                  >
                    {CARRIERS_MODES.map(({ value, label }) => (
                      <div key={value} className="flex items-center gap-3">
                        <RadioGroupItem
                          id={`dup-carriers-${value}`}
                          value={value}
                        />
                        <label
                          htmlFor={`dup-carriers-${value}`}
                          className="text-sm cursor-pointer"
                        >
                          {label}
                        </label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>
                Annuler
              </Button>
              <LoadingButton
                isLoading={isPreviewing}
                disabled={!canProceed}
                onClick={() => handlePreview()}
              >
                Continuer
              </LoadingButton>
            </DialogFooter>
          </>
        ) : step === "codeConflicts" ? (
          <>
            <DialogHeader>
              <DialogTitle>Transporteurs déjà présents</DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-2 text-sm">
              <p>
                <span className="font-semibold">{targetShop?.name}</span> a déjà
                des transporteurs avec ces codes :
              </p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                {conflictingCodes.map((code) => (
                  <li key={code}>{code}</li>
                ))}
              </ul>
              <p>
                Deux transporteurs de même code dans une boutique feraient
                partir les étiquettes avec l'un ou l'autre. Que faire ?
              </p>
              <RadioGroup
                value={codeConflicts}
                onValueChange={(value) =>
                  setCodeConflicts(value as CodeConflicts)
                }
              >
                {CODE_CONFLICTS_CHOICES.map(({ value, label }) => (
                  <div key={value} className="flex items-center gap-3">
                    <RadioGroupItem
                      id={`dup-code-conflicts-${value}`}
                      value={value}
                    />
                    <label
                      htmlFor={`dup-code-conflicts-${value}`}
                      className="cursor-pointer"
                    >
                      {label}
                    </label>
                  </div>
                ))}
              </RadioGroup>
              <p className="text-muted-foreground">
                Rien n'a encore été modifié.
              </p>
            </div>

            <DialogFooter>
              <Button variant="ghost" onClick={() => setStep("select")}>
                Retour
              </Button>
              <LoadingButton
                isLoading={isPreviewing}
                onClick={() => handlePreview(codeConflicts)}
              >
                Continuer
              </LoadingButton>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Confirmer la duplication</DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-2 text-sm">
              <p>
                Source : <span className="font-medium">{sourceShopName}</span>
                {" → "}
                cible :{" "}
                <span className="font-semibold">{targetShop?.name}</span>
              </p>

              {preview && preview.sections.length > 0 && (
                <div className="space-y-1">
                  <p>
                    Sections{" "}
                    <span className="font-semibold text-destructive">
                      écrasées
                    </span>{" "}
                    sur la cible :
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                    {preview.sections.map((section) => (
                      <li key={section}>
                        {SECTION_LABELS[section] ?? section}
                        {section === "overrideRules" &&
                          preview.overrideRules &&
                          ` : ${preview.overrideRules.before} → ${preview.overrideRules.after}`}
                        {section === "routingLocationRules" &&
                          preview.routingLocationRules &&
                          ` : ${preview.routingLocationRules.before} → ${preview.routingLocationRules.after}`}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {preview?.carriers && (
                <div className="space-y-1">
                  <p className="font-medium">Transporteurs</p>
                  {preview.carriers.deleted.length > 0 && (
                    <p className="text-destructive">
                      Supprimés : {formatCarriers(preview.carriers.deleted)}
                    </p>
                  )}
                  {preview.carriers.added.length > 0 && (
                    <p>Ajoutés : {formatCarriers(preview.carriers.added)}</p>
                  )}
                  {preview.carriers.kept.length > 0 && (
                    <p className="text-muted-foreground">
                      Conservés : {formatCarriers(preview.carriers.kept)}
                    </p>
                  )}
                  <p className="text-muted-foreground">
                    Transporteur par défaut ensuite :{" "}
                    {preview.carriers.defaultCode ?? "aucun"}
                  </p>
                </div>
              )}

              {preview?.integrations && (
                <div className="space-y-1">
                  <p className="font-medium">Intégrations</p>
                  {preview.integrations.removed.length > 0 && (
                    <p className="text-destructive">
                      Supprimées (absentes de la source) :{" "}
                      {preview.integrations.removed.join(", ")}
                    </p>
                  )}
                  {preview.integrations.replaced.length > 0 && (
                    <p className="text-destructive">
                      Configuration remplacée :{" "}
                      {preview.integrations.replaced.join(", ")}
                    </p>
                  )}
                  {preview.integrations.added.length > 0 && (
                    <p>Ajoutées : {preview.integrations.added.join(", ")}</p>
                  )}
                </div>
              )}

              {preview?.emailTemplates &&
                (preview.emailTemplates.copied.length > 0 ||
                  preview.emailTemplates.updated.length > 0 ||
                  preview.emailTemplates.kept.length > 0) && (
                  <div className="space-y-1">
                    <p className="font-medium">Templates email retour</p>
                    {preview.emailTemplates.copied.length > 0 && (
                      <p>
                        Copiés dans la cible :{" "}
                        {formatTemplateTitles(preview.emailTemplates.copied)}
                      </p>
                    )}
                    {preview.emailTemplates.updated.length > 0 && (
                      <p>
                        Templates par défaut de la cible modifiés :{" "}
                        {formatTemplateTitles(preview.emailTemplates.updated)}
                      </p>
                    )}
                    {preview.emailTemplates.workflows.length > 0 && (
                      <p>
                        Aussi envoyés par les workflows :{" "}
                        {preview.emailTemplates.workflows.join(", ")}
                      </p>
                    )}
                    {preview.emailTemplates.kept.length > 0 && (
                      <p className="text-muted-foreground">
                        Conservés (même email, ou aucun sur la source) :{" "}
                        {formatTemplateTitles(preview.emailTemplates.kept)}
                      </p>
                    )}
                  </div>
                )}

              {preview?.members && (
                <p>
                  {preview.members.length > 0
                    ? `Invités : ${preview.members.join(", ")}`
                    : "Aucun membre à inviter."}
                </p>
              )}

              <p className="text-muted-foreground">
                L'ancien contenu est sauvegardé (accès staff, 90 jours) et
                chaque changement est tracé dans le journal de la cible.
              </p>
            </div>

            <DialogFooter>
              <Button variant="ghost" onClick={() => setStep("select")}>
                Retour
              </Button>
              <LoadingButton
                isLoading={isPending}
                variant="destructive"
                onClick={() => handleConfirm()}
              >
                Appliquer
              </LoadingButton>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
